// Cave84 — Auth store
// Holds the resolved Firebase user + Firestore user doc + role.
// Components should generally read via the `useAuth` hook, not this store directly,
// because the hook also handles the bootstrap of users/{uid}.

import { create } from 'zustand';
import type { User as FirebaseUser } from 'firebase/auth';
import type { User } from '@cave84/shared-types';

export type AuthStatus = 'initializing' | 'unauthenticated' | 'authenticated';

interface AuthState {
  status: AuthStatus;
  firebaseUser: FirebaseUser | null;
  userDoc: User | null;
  setFirebaseUser: (u: FirebaseUser | null) => void;
  setUserDoc: (u: User | null) => void;
  setStatus: (s: AuthStatus) => void;
  reset: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'initializing',
  firebaseUser: null,
  userDoc: null,
  setFirebaseUser: (firebaseUser) => set({ firebaseUser }),
  setUserDoc: (userDoc) => set({ userDoc }),
  setStatus: (status) => set({ status }),
  reset: () => set({ status: 'unauthenticated', firebaseUser: null, userDoc: null })
}));

// Convenience selectors
export const useIsOwner = () =>
  useAuthStore((s) => s.userDoc?.role === 'owner');

export const useIsAuthenticated = () =>
  useAuthStore((s) => s.status === 'authenticated');
